import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Loader2, Crosshair, ArrowRight, Film } from "lucide-react";
import { API } from "@/lib/api";

export default function ShareClip() {
    const { token } = useParams();
    const [clip, setClip] = useState(null);
    const [state, setState] = useState("loading"); // loading | ok | error

    useEffect(() => {
        let alive = true;
        fetch(`${API}/share/clip/${token}`)
            .then((r) => (r.ok ? r.json() : Promise.reject(r)))
            .then((d) => { if (alive) { setClip(d); setState("ok"); } })
            .catch(() => { if (alive) setState("error"); });
        return () => { alive = false; };
    }, [token]);

    const mediaUrl = `${API}/share/clip/${token}/media`;

    return (
        <div className="min-h-screen bg-obsidian text-white flex flex-col" data-testid="share-clip-page">
            <header className="flex items-center justify-between px-5 lg:px-8 py-4 border-b border-border">
                <Link to="/" className="flex items-center gap-2.5" data-testid="share-logo">
                    <img src="/whitewater-logo.png" alt="WhiteWater AI" className="w-8 h-8" />
                    <div className="leading-none">
                        <div className="font-heading font-bold tracking-tight">WhiteWater<span className="text-volt">AI</span></div>
                        <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">Gameplay Coach</div>
                    </div>
                </Link>
                <Link to="/register" data-testid="share-cta-top"
                    className="h-9 px-4 rounded-sm bg-volt text-black text-sm font-semibold hover:bg-[#33ceff] transition-colors flex items-center gap-1.5">
                    Get Coached <ArrowRight className="w-3.5 h-3.5" />
                </Link>
            </header>

            <main className="flex-1 grid place-items-center px-4 py-10">
                <div className="w-full max-w-3xl">
                    {state === "loading" && (
                        <div className="grid place-items-center py-24" data-testid="share-loading"><Loader2 className="w-7 h-7 animate-spin text-volt" /></div>
                    )}
                    {state === "error" && (
                        <div className="text-center py-24" data-testid="share-error">
                            <Film className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                            <h1 className="font-heading text-2xl font-bold mb-1">Clip unavailable</h1>
                            <p className="text-sm text-muted-foreground">This shared clip doesn't exist or was unshared.</p>
                        </div>
                    )}
                    {state === "ok" && clip && (
                        <div className="ww-fade-up">
                            <div className="flex items-center gap-2 mb-3">
                                <span className="text-[11px] font-mono uppercase tracking-wider text-volt bg-volt/10 border border-volt/40 px-2 py-0.5 rounded-sm">{clip.event_type}</span>
                                <span className="text-xs text-muted-foreground">{clip.game}</span>
                            </div>
                            <h1 className="font-heading text-3xl sm:text-4xl font-bold mb-4" data-testid="share-title">{clip.title}</h1>

                            <div className="relative aspect-video bg-black border border-border rounded-sm overflow-hidden ww-grid-bg">
                                {clip.has_video ? (
                                    <video src={mediaUrl} controls playsInline className="w-full h-full" data-testid="share-video" />
                                ) : clip.thumbnail_url ? (
                                    <img src={clip.thumbnail_url} alt={clip.title} className="w-full h-full object-cover" data-testid="share-image" />
                                ) : clip.has_media ? (
                                    <img src={mediaUrl} alt={clip.title} className="w-full h-full object-cover" data-testid="share-image" />
                                ) : (
                                    <div className="w-full h-full grid place-items-center"><Film className="w-10 h-10 text-muted-foreground" /></div>
                                )}
                                {/* Watermark overlay */}
                                <div className="absolute bottom-3 right-3 flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-black/55 backdrop-blur-sm border border-white/10 pointer-events-none" data-testid="share-watermark">
                                    <Crosshair className="w-3.5 h-3.5 text-volt" />
                                    <span className="text-[11px] font-mono tracking-wide text-white/90">WhiteWater AI</span>
                                </div>
                            </div>

                            <div className="mt-8 rounded-sm border border-border bg-surface p-6 text-center">
                                <h2 className="text-base md:text-lg font-heading font-bold mb-1">Want clips like this — and a coach that watches every match?</h2>
                                <p className="text-sm text-muted-foreground mb-4">WhiteWater AI auto-captures your best plays and coaches you in real time.</p>
                                <Link to="/register" data-testid="share-cta-bottom"
                                    className="inline-flex h-11 px-6 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] transition-colors items-center gap-2">
                                    Start free <ArrowRight className="w-4 h-4" />
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </main>

            <footer className="border-t border-border px-4 py-4 text-center">
                <p className="text-[11px] text-muted-foreground font-mono tracking-wide">
                    &copy; WhiteWater AI 2026 &middot; Veteran Owned and Operated <span aria-label="American flag">&#127482;&#127480;</span>
                </p>
            </footer>
        </div>
    );
}
